/**
 * Navigation handling utilities for step/substep transitions
 */
import { getStepId, getSubstepId } from './getters';
import {
  findFirstIncompleteSubstep,
  findFirstRealSubstep,
  findStepForSubstep,
  findSubstepById
} from './stepFinders';
import { buildNavigationUrl, urlNeedsUpdate } from './urlUtils';

/**
 * Internal helper: navigate to the substep that contains targetSubstep.
 * Returns false if no valid target/parent step is found.
 */
const navigateToTargetSubstep = ({ stepsState, baseUrl, navigate, targetSubstep, params }) => {
  if (!targetSubstep) return false;

  const stepForSubstep = findStepForSubstep(stepsState, targetSubstep.code);
  if (!stepForSubstep) return false;

  const stepId = getStepId(stepForSubstep);
  const substepId = getSubstepId(targetSubstep);
  const hasSubsteps = stepForSubstep.substeps?.length > 0;

  const url = hasSubsteps
    ? buildNavigationUrl(baseUrl, stepId, substepId)
    : buildNavigationUrl(baseUrl, stepId);

  if (urlNeedsUpdate(params, stepId, hasSubsteps ? substepId : null)) {
    navigate(url, { replace: true });
  }
  return true;
};

/**
 * Handles navigation when no step_id is present in URL.
 * Navigates to the first non-special substep.
 */
export const handleMissingStepId = ({ flattenedSubsteps, stepsState, baseUrl, navigate, params }) =>
  navigateToTargetSubstep({
    stepsState,
    baseUrl,
    navigate,
    params,
    targetSubstep: findFirstRealSubstep(flattenedSubsteps, stepsState)
  });

/**
 * Handles navigation when step_id is invalid.
 * Navigates to the first available substep.
 */
export const handleInvalidStepId = ({ flattenedSubsteps, stepsState, baseUrl, navigate, params }) =>
  navigateToTargetSubstep({
    stepsState,
    baseUrl,
    navigate,
    params,
    targetSubstep: flattenedSubsteps[0]
  });

/**
 * Navigates to the first incomplete (non-completed, non-special) step.
 * Falls back to the first real substep if all steps are completed.
 */
export const handleFirstIncompleteStep = ({
  flattenedSubsteps,
  stepsState,
  baseUrl,
  navigate,
  params
}) => {
  const incompleteSubstep = findFirstIncompleteSubstep(flattenedSubsteps, stepsState);
  const targetSubstep = incompleteSubstep || findFirstRealSubstep(flattenedSubsteps, stepsState);

  return navigateToTargetSubstep({ stepsState, baseUrl, navigate, params, targetSubstep });
};

/**
 * Handles navigation for a step that has substeps.
 * Falls back to first substep when none provided / invalid.
 */
export const handleStepWithSubsteps = ({
  targetStep,
  stepId,
  substepId,
  baseUrl,
  navigate,
  navigateToSubstep,
  isNavigatingFromUrl,
  params
}) => {
  const navigateToFirstSubstep = () => {
    const firstSubstep = targetStep.substeps[0];
    const firstSubstepId = getSubstepId(firstSubstep);
    if (urlNeedsUpdate(params, stepId, firstSubstepId)) {
      navigate(buildNavigationUrl(baseUrl, stepId, firstSubstepId), { replace: true });
    }
    return true;
  };

  if (!substepId) return navigateToFirstSubstep();

  const targetSubstep = findSubstepById(targetStep, substepId);
  if (!targetSubstep) return navigateToFirstSubstep();

  if (!targetSubstep.active) {
    isNavigatingFromUrl.current = true;
    navigateToSubstep(targetSubstep.code, { fromUrl: true });
  }

  return true;
};

/**
 * Handles navigation for a step without substeps.
 * Strips a trailing substep_id from the URL if present.
 */
export const handleStepWithoutSubsteps = ({
  targetStep,
  stepId,
  substepId,
  baseUrl,
  navigate,
  activateStep,
  isNavigatingFromUrl,
  params
}) => {
  if (substepId) {
    if (urlNeedsUpdate(params, stepId)) {
      navigate(buildNavigationUrl(baseUrl, stepId), { replace: true });
    }
    return true;
  }

  if (!targetStep.active) {
    isNavigatingFromUrl.current = true;
    activateStep(targetStep.code, { fromUrl: true });
  }

  return true;
};

/**
 * Syncs the URL based on current active step/substep state.
 */
export const syncUrlFromState = ({ activeStep, activeSubstep, params, baseUrl, navigate }) => {
  if (!activeStep) return;

  const stepId = getStepId(activeStep);
  const hasSubsteps = activeStep.substeps?.length > 0;

  if (hasSubsteps && activeSubstep) {
    const substepId = getSubstepId(activeSubstep);
    if (urlNeedsUpdate(params, stepId, substepId)) {
      navigate(buildNavigationUrl(baseUrl, stepId, substepId), { replace: true });
    }
    return;
  }

  if (urlNeedsUpdate(params, stepId)) {
    navigate(buildNavigationUrl(baseUrl, stepId), { replace: true });
  }
};
